import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, CheckCircle, Building2, MapPin, FileText, Globe, User, Percent } from 'lucide-react';
import { businessService } from '../../services/business-service';
import { commissionService } from '../../services/commission-service';
import { StatusBadge } from '../../components/common/StatusBadge';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { handleApiError } from '../../lib/api-client';

export function BusinessDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    action: 'enable' | 'disable';
  }>({
    isOpen: false,
    action: 'enable',
  });
  const [reason, setReason] = useState('');

  const { data: business, isLoading, error } = useQuery({
    queryKey: ['business', id],
    queryFn: () => businessService.getById(id!),
    enabled: !!id,
  });

  const setStatusMutation = useMutation({
    mutationFn: ({ isEnabled, reason }: { isEnabled: boolean; reason?: string }) =>
      businessService.setStatus(id!, { isEnabled, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business', id] });
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setConfirmDialog({ isOpen: false, action: 'enable' });
      setReason('');
    },
  });

  const handleStatusChange = (action: 'enable' | 'disable') => {
    setConfirmDialog({ isOpen: true, action });
  };

  const handleConfirmStatusChange = () => {
    const isEnabled = confirmDialog.action === 'enable';
    setStatusMutation.mutate({
      isEnabled,
      reason: isEnabled ? undefined : reason,
    });
  };

  // SW-98: commission rate + signed contract — staff-only.
  const { data: commissionHistory } = useQuery({
    queryKey: ['commission', id],
    queryFn: () => commissionService.getHistory(id!),
    enabled: !!id,
  });

  const [rateForm, setRateForm] = useState({ ratePercent: '', effectiveFrom: '', effectiveTo: '' });
  const [signedContractFile, setSignedContractFile] = useState<File | null>(null);
  const [tPayOfferFile, setTPayOfferFile] = useState<File | null>(null);
  const [commissionError, setCommissionError] = useState<string | null>(null);

  const setRateMutation = useMutation({
    mutationFn: () =>
      commissionService.setRate(id!, {
        ratePercent: parseFloat(rateForm.ratePercent),
        effectiveFrom: rateForm.effectiveFrom,
        effectiveTo: rateForm.effectiveTo || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission', id] });
      setRateForm({ ratePercent: '', effectiveFrom: '', effectiveTo: '' });
      setCommissionError(null);
    },
    onError: (err) => setCommissionError(handleApiError(err)),
  });

  const uploadContractMutation = useMutation({
    mutationFn: () => commissionService.uploadContract(id!, signedContractFile!, tPayOfferFile ?? undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission', id] });
      setSignedContractFile(null);
      setTPayOfferFile(null);
      setCommissionError(null);
    },
    onError: (err) => setCommissionError(handleApiError(err)),
  });

  const today = new Date().toISOString().slice(0, 10);
  const currentRate = commissionHistory?.find(
    (r) => r.effectiveFrom <= today && (!r.effectiveTo || r.effectiveTo >= today)
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-ink-muted">Loading business details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-brand-warm/20 border border-brand-warm/40 text-brand-warm px-4 py-3 rounded">
        Error loading business: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  if (!business) {
    return (
      <div className="bg-white/10 border border-white/30 text-ink-primary px-4 py-3 rounded">
        Business not found
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/businesses')}
            className="p-2 text-ink-secondary hover:bg-white/10 rounded-lg"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h2 className="font-display text-2xl font-semibold tracking-tight text-ink-primary">{business.companyName}</h2>
            <p className="text-ink-secondary mt-1">{business.displayName}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <StatusBadge isEnabled={business.isEnabled} />
          {business.isEnabled ? (
            <button
              onClick={() => handleStatusChange('disable')}
              className="flex items-center gap-2 px-4 py-2 bg-brand-warm text-white rounded-lg hover:bg-brand-warm/90"
            >
              <Ban size={18} />
              Disable Business
            </button>
          ) : (
            <button
              onClick={() => handleStatusChange('enable')}
              className="flex items-center gap-2 px-4 py-2 bg-brand-confirm text-white rounded-lg hover:bg-brand-confirm/90"
            >
              <CheckCircle size={18} />
              Enable Business
            </button>
          )}
        </div>
      </div>

      {/* Basic Information */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-brand-primary/20 text-brand-primary-light">
            <Building2 size={24} />
          </div>
          <h3 className="font-display text-lg font-semibold tracking-tight text-ink-primary">Business Information</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">Company Name</label>
            <p className="text-ink-primary">{business.companyName}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">Display Name</label>
            <p className="text-ink-primary">{business.displayName}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">NIP</label>
            <p className="text-ink-primary">{business.nip || '-'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">User Email</label>
            <p className="text-ink-primary">{business.userEmail || '-'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">Created</label>
            <p className="text-ink-primary">
              {new Date(business.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">Last Updated</label>
            <p className="text-ink-primary">
              {new Date(business.updatedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Location */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-brand-confirm/20 text-brand-confirm">
            <MapPin size={24} />
          </div>
          <h3 className="font-display text-lg font-semibold tracking-tight text-ink-primary">Location</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">City</label>
            <p className="text-ink-primary">{business.city || '-'}</p>
          </div>
        </div>
      </div>

      {/* Website */}
      {(business.website || business.websiteDescription) && (
        <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-brand-primary-light/20 text-brand-primary-light">
              <Globe size={24} />
            </div>
            <h3 className="font-display text-lg font-semibold tracking-tight text-ink-primary">Website</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {business.website && (
              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">URL</label>
                <a
                  href={business.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-primary-light hover:text-white hover:underline break-all"
                >
                  {business.website}
                </a>
              </div>
            )}
            {business.websiteDescription && (
              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">Description</label>
                <p className="text-ink-primary">{business.websiteDescription}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contact Person */}
      {(business.contactPersonName || business.contactPersonSurname) && (
        <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-brand-primary/20 text-brand-primary-light">
              <User size={24} />
            </div>
            <h3 className="font-display text-lg font-semibold tracking-tight text-ink-primary">Contact Person</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {business.contactPersonName && (
              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">First Name</label>
                <p className="text-ink-primary">{business.contactPersonName}</p>
              </div>
            )}
            {business.contactPersonSurname && (
              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">Last Name</label>
                <p className="text-ink-primary">{business.contactPersonSurname}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Integration & Features */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-brand-accent/20 text-brand-accent">
            <FileText size={24} />
          </div>
          <h3 className="font-display text-lg font-semibold tracking-tight text-ink-primary">Integrations & Features</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">TPay Integration</label>
            <p className="text-ink-primary">
              {business.hasTPay ? (
                <span className="text-brand-confirm font-medium">Enabled</span>
              ) : (
                <span className="text-ink-muted">Disabled</span>
              )}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">KSeF Integration</label>
            <p className="text-ink-primary">
              {business.hasKSeF ? (
                <span className="text-brand-confirm font-medium">Enabled</span>
              ) : (
                <span className="text-ink-muted">Disabled</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Commission & Contract (SW-98) — staff-only, never partner-writable */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-brand-confirm/20 text-brand-confirm">
            <Percent size={24} />
          </div>
          <h3 className="font-display text-lg font-semibold tracking-tight text-ink-primary">Commission & Contract</h3>
        </div>

        {commissionError && (
          <div className="mb-4 bg-brand-warm/20 border border-brand-warm/40 text-brand-warm px-4 py-3 rounded">
            {commissionError}
          </div>
        )}

        <div className="mb-6">
          <label className="block text-sm font-medium text-ink-secondary mb-1">Current rate</label>
          {currentRate ? (
            <p className="text-ink-primary">
              <span className="text-2xl font-bold">{currentRate.ratePercent}%</span>{' '}
              <span className="text-ink-secondary text-sm">
                ({currentRate.source}, {currentRate.effectiveFrom.slice(0, 10)}
                {currentRate.effectiveTo ? ` – ${currentRate.effectiveTo.slice(0, 10)}` : ' – indefinite'})
              </span>
              {currentRate.signedContractUrl && (
                <>
                  {' · '}
                  <a
                    href={currentRate.signedContractUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-primary-light hover:text-white hover:underline"
                  >
                    signed contract
                  </a>
                </>
              )}
              {currentRate.tPayOfferUrl && (
                <>
                  {' · '}
                  <a
                    href={currentRate.tPayOfferUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-primary-light hover:text-white hover:underline"
                  >
                    TPay offer
                  </a>
                </>
              )}
            </p>
          ) : (
            <p className="text-ink-muted">No active agreement — default 3% applies (reporting only).</p>
          )}
        </div>

        {/* Set a new rate period */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setRateMutation.mutate();
          }}
          className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end mb-6"
        >
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">Rate %</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              required
              value={rateForm.ratePercent}
              onChange={(e) => setRateForm((f) => ({ ...f, ratePercent: e.target.value }))}
              className="w-full px-3 py-2 bg-surface-input border border-white/15 text-ink-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">Effective from</label>
            <input
              type="date"
              required
              value={rateForm.effectiveFrom}
              onChange={(e) => setRateForm((f) => ({ ...f, effectiveFrom: e.target.value }))}
              className="w-full px-3 py-2 bg-surface-input border border-white/15 text-ink-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">Effective to (optional)</label>
            <input
              type="date"
              value={rateForm.effectiveTo}
              onChange={(e) => setRateForm((f) => ({ ...f, effectiveTo: e.target.value }))}
              className="w-full px-3 py-2 bg-surface-input border border-white/15 text-ink-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>
          <button
            type="submit"
            disabled={setRateMutation.isPending}
            className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 disabled:opacity-50"
          >
            {setRateMutation.isPending ? 'Saving...' : 'Set Rate'}
          </button>
        </form>

        {/* Upload signed contract / TPay offer */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            uploadContractMutation.mutate();
          }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mb-6"
        >
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">Signed contract (PDF)</label>
            <input
              type="file"
              accept="application/pdf"
              required
              onChange={(e) => setSignedContractFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm text-ink-primary file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-brand-primary file:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">TPay offer (optional, PDF)</label>
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setTPayOfferFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm text-ink-primary file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-white/10 file:text-ink-primary"
            />
          </div>
          <button
            type="submit"
            disabled={!signedContractFile || uploadContractMutation.isPending}
            className="px-4 py-2 bg-brand-confirm text-white rounded-lg hover:bg-brand-confirm/90 disabled:opacity-50"
          >
            {uploadContractMutation.isPending ? 'Uploading...' : 'Upload'}
          </button>
        </form>

        {/* History */}
        {commissionHistory && commissionHistory.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-2">History</label>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-ink-secondary border-b border-white/10">
                  <th className="pb-2 pr-4">Rate</th>
                  <th className="pb-2 pr-4">From</th>
                  <th className="pb-2 pr-4">To</th>
                  <th className="pb-2">Source</th>
                </tr>
              </thead>
              <tbody>
                {commissionHistory.map((r, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-2 pr-4 text-ink-primary">{r.ratePercent}%</td>
                    <td className="py-2 pr-4 text-ink-primary">{r.effectiveFrom.slice(0, 10)}</td>
                    <td className="py-2 pr-4 text-ink-primary">{r.effectiveTo ? r.effectiveTo.slice(0, 10) : 'indefinite'}</td>
                    <td className="py-2 text-ink-primary">{r.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
          <p className="text-sm text-ink-secondary mb-2">Facilities</p>
          <p className="text-3xl font-bold text-ink-primary">{business.facilityCount}</p>
        </div>

        <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
          <p className="text-sm text-ink-secondary mb-2">Products</p>
          <p className="text-3xl font-bold text-ink-primary">{business.productCount}</p>
        </div>
      </div>

      {/* Disabled Info */}
      {!business.isEnabled && business.disabledAt && (
        <div className="bg-brand-warm/15 border border-brand-warm/40 rounded-lg p-6">
          <h3 className="font-display text-lg font-semibold tracking-tight text-brand-warm mb-4">Disabled Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-brand-warm mb-1">Disabled At</label>
              <p className="text-ink-primary">
                {new Date(business.disabledAt).toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
            {business.disabledReason && (
              <div>
                <label className="block text-sm font-medium text-brand-warm mb-1">Reason</label>
                <p className="text-ink-primary">{business.disabledReason}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => {
          setConfirmDialog({ isOpen: false, action: 'enable' });
          setReason('');
        }}
        onConfirm={handleConfirmStatusChange}
        title={confirmDialog.action === 'enable' ? 'Enable Business' : 'Disable Business'}
        message={`Are you sure you want to ${confirmDialog.action} "${business.companyName}"?`}
        confirmText={confirmDialog.action === 'enable' ? 'Enable' : 'Disable'}
        confirmVariant={confirmDialog.action === 'disable' ? 'danger' : 'primary'}
        isLoading={setStatusMutation.isPending}
      >
        {confirmDialog.action === 'disable' && (
          <div className="mt-4">
            <label htmlFor="reason" className="block text-sm font-medium text-white/70 mb-2">
              Reason for disabling (optional)
            </label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 bg-surface-input border border-white/15 text-ink-primary placeholder-ink-muted rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              rows={3}
              placeholder="Enter reason..."
            />
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
}
